import { Dialog } from '@ariakit/react';
import type { DialogProps } from '@ariakit/react';
import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormikContext } from 'formik';
import type { FC, MouseEventHandler } from 'react';
import { useEffect, useState } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { ActionModeProvider } from 'src/components/action-mode';
import { ActionGroupModeProvider } from 'src/components/buttons-and-actions/action-group';
import type { ButtonProps } from 'src/components/buttons-and-actions/button';
import { Button } from 'src/components/buttons-and-actions/button';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useMeasure } from 'src/hooks/use-measure';
import { ParentZIndexProvider } from 'src/hooks/use-parent-z-index';
import { Colors } from 'src/styles';
import { cn } from 'src/utils/cn';
import type { StrictOmit } from 'src/utils/utility-types';

const borderRadius = '0.6rem';

const padding = '1.5rem';

// see https://github.com/ariakit/ariakit/issues/4345#issuecomment-2579469808
const layoutFix = 'prisma-layout-fix-for-react-dialog';

const LayoutFix = createGlobalStyle`
  html.${layoutFix} {
    overflow-x: visible;

    body {
      padding-right: 0 !important;
    }
  }
`;

type Size = 'default' | 'small' | 'middle' | 'large';

const sizeWidths: Record<Size, string> = {
  default: '60rem',
  small: '30rem',
  middle: '45rem',
  large: '90rem',
};

const StyledDialog = styled(Dialog)<{ size: Size }>`
  position: fixed;
  inset: 0;
  height: fit-content;

  margin: 10vh auto;
  width: ${({ size }) => sizeWidths[size]};
  max-width: calc(100% - ${padding} * 2);
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.5);
  border-radius: ${borderRadius};
  transition: opacity 0.1s ease-in-out;
`;

const Backdrop = styled.div`
  background: #00000054;
`;

const HeaderWrapper = styled.div<{ hasIcon: boolean }>`
  border-top-left-radius: ${borderRadius};
  border-top-right-radius: ${borderRadius};
  background-color: ${Colors.brand};
  color: white;

  display: grid;
  grid-auto-flow: column;
  grid-gap: ${padding};
  padding: ${padding};
  align-items: center;

  ${({ hasIcon }) => css`
    grid-template-columns: auto ${hasIcon ? '1fr' : 'min-content'};
  `}

  font-size: 2rem;
`;

const Title = styled.p`
  font-weight: 900;
  margin: 0;
  word-break: break-all;
`;

const IconCloseButton = styled.button`
  align-self: start;
  color: ${Colors.brandLight1};
  background: none;
  border: none;
  padding: 0;
  font-size: 2.2rem;
  justify-items: space-around;

  cursor: pointer;
  user-select: none;

  transition:
    color 0.35s,
    background-color 0.35s;

  &:hover {
    color: ${Colors.brandLight2};
  }
`;

const ContentWrapper = styled.div<{ scrollable?: boolean; footer: boolean }>`
  ${({ scrollable }) =>
    scrollable &&
    css`
      max-height: 50vh;
      overflow-y: auto;
    `}

  ${({ footer }) =>
    !footer &&
    css`
      border-bottom-left-radius: ${borderRadius};
      border-bottom-right-radius: ${borderRadius};
    `}

  background-color: white;
  padding: ${padding};
`;

const FooterWrapper = styled.div`
  border-bottom-left-radius: ${borderRadius};
  border-bottom-right-radius: ${borderRadius};
  background-color: white;
  padding: ${padding};
`;

type SubmitButtonProps = {
  label: string;
  dataTestId?: string;
  disabled?: boolean;
  props?: ButtonProps;
};

type CloseButtonProps = {
  label?: string;
  dataTestId?: string;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  props?: ButtonProps;
};

type ConfirmButtonProps = {
  onClick: MouseEventHandler<HTMLButtonElement>;
  label: string;
  dataTestId?: string;
  disabled?: boolean;
  props?: ButtonProps;
  tooltip?: string | boolean;
};

type Footer =
  | {
      submitButton: SubmitButtonProps;
      closeButton?: CloseButtonProps | null;
      confirmButton?: never;
    }
  | {
      confirmButton: ConfirmButtonProps | null;
      closeButton?: CloseButtonProps | null;
      submitButton?: never;
    };

type Props = {
  icon?: IconDefinition | undefined;
  title: string;
  size?: Size;
  /**
   * Be very careful with this one. Usually there is no good reason why you should
   * NOT be able to close a modal. At the end you cannot force people to do something
   * within the modal or keep it open for some time. They could simply refresh or navigate
   * away from the page.
   */
  hideClose?: boolean;
  /**
   * You can use the default footer by just passing in the confirmationButton specification.
   * * By default, closeButton is a button that closes the modal when clicked.
   * * If you set closeButton to null explicitly, you will have no close button.
   * * If your modal has a form, use submitButton instead of confirmButton. */
  footer: Footer | null;
  onDismiss?: () => void;
} & StrictOmit<DialogProps, 'open' | 'backdrop' | 'onClose' | 'style'>;

export const Modal: FC<Props> = ({
  icon,
  title,
  size = 'default',
  hideClose,
  footer,
  onDismiss,
  children,
  ...dialogProps
}) => {
  useEffect(() => {
    document.documentElement.classList.add(layoutFix);
    return () => document.documentElement.classList.remove(layoutFix);
  }, []);

  const [isScrollable, setIsScrollable] = useState(false);
  const [innerContentRef, innerContentBounds] = useMeasure();

  useEffect(() => {
    if (innerContentBounds.height > 300) {
      setIsScrollable(true);
    } else {
      setIsScrollable(false);
    }
  }, [innerContentBounds]);

  const zIndex = 3;

  return (
    // reset ActionModeProvider and ActionGroupModeProvider in case
    // a modal gets nested inside them
    <ActionModeProvider value={null}>
      <ActionGroupModeProvider value="single">
        <LayoutFix />

        <StyledDialog
          {...dialogProps}
          open
          backdrop={<Backdrop />}
          onClose={onDismiss}
          size={size}
          style={{ zIndex }}
        >
          <ParentZIndexProvider value={zIndex}>
            {title && (
              <HeaderWrapper hasIcon={Boolean(icon)}>
                {icon && <FontAwesomeIcon icon={icon} />}
                <Title>{title}</Title>
                {!hideClose && (
                  <IconCloseButton onClick={onDismiss}>
                    <FontAwesomeIcon icon={faTimes} aria-label="Close modal" />
                  </IconCloseButton>
                )}
              </HeaderWrapper>
            )}

            <ContentWrapper scrollable={isScrollable} footer={!!footer}>
              {/* we need this div to measure the children's height and set the parent div to scrollable */}
              <div ref={innerContentRef}>{children}</div>
            </ContentWrapper>

            {footer && (
              <FooterWrapper>
                <div
                  className={cn(
                    `
                      grid grid-flow-col items-center gap-xl
                      legacy:gap-[2rem]
                    `,
                    footer.closeButton === null
                      ? 'justify-end'
                      : 'justify-between'
                  )}
                >
                  {footer.closeButton === undefined ? (
                    <Button
                      mode="secondary"
                      data-testid="close-modal"
                      onClick={onDismiss}
                    >
                      Close
                    </Button>
                  ) : footer.closeButton ? (
                    <Button
                      mode="secondary"
                      data-testid={footer.closeButton.dataTestId}
                      onClick={footer.closeButton.onClick ?? onDismiss}
                      disabled={footer.confirmButton?.disabled}
                      {...footer.closeButton.props}
                    >
                      {footer.closeButton.label ?? 'Close'}
                    </Button>
                  ) : null}
                  {footer.submitButton && (
                    <SubmitButton submitButton={footer.submitButton} />
                  )}
                  {footer.confirmButton && (
                    <ConfirmButton confirmButton={footer.confirmButton} />
                  )}
                </div>
              </FooterWrapper>
            )}
          </ParentZIndexProvider>
        </StyledDialog>
      </ActionGroupModeProvider>
    </ActionModeProvider>
  );
};

const SubmitButton: FC<{ submitButton: SubmitButtonProps }> = ({
  submitButton,
}) => {
  const { submitForm } = useFormikContext();

  return (
    <Button
      disabled={submitButton.disabled}
      type="submit"
      data-testid={submitButton.dataTestId ?? 'submit-form'}
      mode="primary"
      onClick={() => submitForm()}
      {...submitButton.props}
    >
      {submitButton.label}
    </Button>
  );
};

const ConfirmButton: FC<{ confirmButton: ConfirmButtonProps }> = ({
  confirmButton,
}) => (
  <Tooltip content={confirmButton.tooltip}>
    {(targetProps) => (
      <Button
        disabled={confirmButton.disabled}
        data-testid={confirmButton.dataTestId ?? 'submit-form'}
        mode="primary"
        onClick={confirmButton.onClick}
        {...confirmButton.props}
        {...targetProps}
      >
        {confirmButton.label}
      </Button>
    )}
  </Tooltip>
);
